import {isAdminView} from '../admin-view.js';
/**
 * useApi -- Fetch wrapper with auth, loading state, and error handling.
 *
 * Uses Bearer token from localStorage (cross-domain compatible).
 * Redirects to /login on 401.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { authHeaders, clearToken } from './useAuth';
import { loginDestination } from '../owner-navigation.js';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

function handle401() {
  if(isAdminView)throw Error('Owner view expired. Reconnect it from admin.');
  clearToken();
  window.location.href = loginDestination(window.location.pathname + window.location.search);
}

export function useApi(endpoint, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!options.skip);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const fetchData = useCallback(async (overrideEndpoint) => {
    const url = `${API_BASE}${overrideEndpoint || endpoint}`;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let timedOut = false;
    const timeout = options.timeoutMs ? setTimeout(() => { timedOut = true; controller.abort(); }, options.timeoutMs) : null;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(url, {
        signal: controller.signal,
        headers: { 'Accept': 'application/json', ...authHeaders() },
      });

      if (res.status === 401) { handle401(); return; }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(errData.error || `Request failed: ${res.status}`);
      }

      const json = await res.json();
      if(abortRef.current !== controller) return;
      setData(json);
      return json;
    } catch (err) {
      if (err.name === 'AbortError' && !timedOut) return;
      if(abortRef.current === controller) { setData(null); setError(timedOut ? 'The request took too long. Refresh to try again.' : err.message); }
    } finally {
      if (timeout) clearTimeout(timeout);
      if(abortRef.current === controller) setLoading(false);
    }
  }, [endpoint, options.timeoutMs]);

  useEffect(() => {
    if (!options.skip) fetchData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData, options.skip, ...(Array.isArray(options.deps) ? options.deps : [])]);

  return { data, loading, error, refetch: fetchData };
}

export function useMutation(endpoint, method = 'POST') {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const execute = useCallback(async (body) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...authHeaders() },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (res.status === 401) { handle401(); return null; }

      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.error || `Request failed: ${res.status}`);

      if(abortRef.current !== controller) return;
      setData(json);
      return json;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [endpoint, method]);

  return { execute, loading, error, data };
}

export async function apiFetch(endpoint, options = {}) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json', ...authHeaders() },
    ...options,
  });

  if (res.status === 401) { handle401(); return null; }
  return res.json();
}
